import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HardHat, Loader2, Plus, Trash2, Save } from "lucide-react";

export default function ManodoperaCosts() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/settings/manodopera").then((r) => setTiers(r.data.tiers || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setRow = (i, k, v) => setTiers((t) => t.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const addRow = () => setTiers((t) => [...t, { da_kw: "", a_kw: "", prezzo: "" }]);
  const removeRow = (i) => setTiers((t) => t.filter((_, idx) => idx !== i));

  const save = async () => {
    const payload = {
      tiers: tiers.map((t) => ({
        da_kw: Number(t.da_kw),
        a_kw: t.a_kw === "" || t.a_kw === null || t.a_kw === undefined ? null : Number(t.a_kw),
        prezzo: Number(t.prezzo),
      })),
    };
    if (payload.tiers.some((t) => Number.isNaN(t.da_kw) || Number.isNaN(t.prezzo) || (t.a_kw !== null && Number.isNaN(t.a_kw)))) {
      toast.error("Compila potenza da/prezzo per ogni fascia (lascia 'A' vuoto per l'ultima fascia illimitata)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/manodopera", payload);
      setTiers(data.tiers);
      toast.success("Listino manodopera salvato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Manodopera</h1>
          <p className="text-xs text-muted-foreground">Listino manodopera (€/kW) per fascia di potenza impianto</p>
        </div>
        <Button onClick={save} disabled={saving || loading} data-testid="save-manodopera-btn" className="font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva listino
        </Button>
      </header>

      <div className="p-4 lg:p-8">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <Card className="rounded border-border p-5" data-testid="manodopera-card">
            <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-bold mb-4 flex items-center gap-2">
              <HardHat className="h-4 w-4 text-primary" strokeWidth={1.5} /> Fasce di potenza
            </h2>
            <p className="text-xs text-muted-foreground mb-5">Il costo della manodopera è applicato al preventivo (lead e progetti) come <b>€/kW × potenza impianto (kWp)</b>, scegliendo la prima fascia il cui limite <b>"A kW" ≥</b> potenza. Lascia il campo <b>"A"</b> vuoto nell'ultima fascia per indicare "da … in su" (nessun limite superiore).</p>

            <div className="hidden sm:grid grid-cols-[140px_140px_160px_40px] gap-3 px-1 pb-2 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
              <div>Da (kW)</div><div>A (kW)</div><div>Prezzo (€/kW)</div><div></div>
            </div>

            <div className="space-y-2">
              {tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[140px_140px_160px_40px] gap-3 items-center" data-testid={`manodopera-row-${i}`}>
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Da (kW)</Label>
                    <Input type="number" value={t.da_kw} onChange={(e) => setRow(i, "da_kw", e.target.value)} data-testid={`manodopera-da-${i}`} placeholder="0" />
                  </div>
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">A (kW)</Label>
                    <Input type="number" value={t.a_kw ?? ""} onChange={(e) => setRow(i, "a_kw", e.target.value)} data-testid={`manodopera-a-${i}`} placeholder="in su" />
                  </div>
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="sm:hidden text-[10px] uppercase tracking-widest text-muted-foreground">Prezzo (€/kW)</Label>
                    <Input type="number" value={t.prezzo} onChange={(e) => setRow(i, "prezzo", e.target.value)} data-testid={`manodopera-prezzo-${i}`} placeholder="250" />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeRow(i)} data-testid={`manodopera-remove-${i}`} className="text-destructive hover:text-destructive justify-self-end col-span-2 sm:col-span-1">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" size="sm" onClick={addRow} data-testid="add-manodopera-btn" className="mt-4">
              <Plus className="h-4 w-4 mr-1.5" /> Aggiungi fascia
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
